
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { CircleCheck, Phone, MapPin } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { submitToPabbly } from '@/utils/pabblyConnect';
import { toast } from '@/hooks/use-toast';

const schemaData = [{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Astrem Web Design Services",
  "image": "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/evan_profile.jpg",
  "description": "Professional web design services in Baton Rouge, LA. Custom websites, e-commerce solutions, and digital marketing services.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "123 Main Street",
    "addressLocality": "Baton Rouge",
    "addressRegion": "LA",
    "postalCode": "70801",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 30.4515,
    "longitude": -91.1871
  },
  "priceRange": "$$$",
  "telephone": "225-331-6114"
}];

const portfolioItems = [
  {
    title: "The Vladimir Collection",
    image: "https://app.milanote.com/media/p/images/1TZl4J1uaXLgcv/VFq/combined_screenshots_with_drawing%20%288%29.jpg",
    description: "Luxury real estate website"
  },
  {
    title: "Pinnacle Roofing",
    image: "https://app.milanote.com/media/p/images/1TZl4J1uaXLgcw/HWE/combined_screenshots_with_drawing%20%289%29.jpg",
    description: "Construction company website"
  },
  {
    title: "H.A. May Construction",
    image: "https://app.milanote.com/media/p/images/1TZlHu1uaXLgcx/1pk/combined_screenshots_with_drawing%20%2810%29.jpg",
    description: "Construction services website"
  }
];

const features = [
  "Custom Website Design",
  "Mobile-First Development",
  "SEO Optimization",
  "Fast Loading Times",
  "Content Management Systems",
  "E-commerce Solutions",
  "Security Features",
  "Analytics Integration"
];

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message?: string;
};

const WebDesignService = () => {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      await submitToPabbly(data, 'web_design_landing_form');
      
      // Google Tag Manager form submission event
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'form_submission',
          'form_name': 'web_design_landing',
          'submission_page': window.location.pathname
        });
      }
      
      reset();
      
      toast({
        title: "Message sent!",
        description: "We'll be in touch with you shortly.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <PageLayout 
      title="Professional Web Design Services in Baton Rouge | Astrem"
      description="Transform your online presence with our professional web design services in Baton Rouge. Custom websites, e-commerce solutions, and digital marketing expertise."
      schemaData={schemaData}
    >
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-zinc-900 to-zinc-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-b from-black to-transparent"></div>
          {/* Grid pattern overlay */}
          <div className="h-full w-full" 
               style={{ 
                 backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")",
               }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="space-y-6">
                <span className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium">
                  Web Design Services
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium text-white">
                  Transform Your Online Presence
                </h1>
                <p className="text-xl text-gray-300 max-w-xl">
                  Custom-built websites that drive results. Stand out from the competition with a website that converts visitors into customers.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 md:p-8 border border-white/20">
                <h3 className="text-xl font-medium text-white mb-6">Start Your Project</h3>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        {...register("firstName", { required: true })}
                        placeholder="First Name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <Input
                        {...register("lastName", { required: true })}
                        placeholder="Last Name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                  <Input
                    {...register("email", { required: true })}
                    type="email"
                    placeholder="Email Address"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Input
                    {...register("phone", { required: true })}
                    placeholder="Phone Number"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Textarea
                    {...register("message")}
                    placeholder="Tell us about your project (optional)"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Get Started"}
                  </Button>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-16">
              Everything You Need for a Successful Online Presence
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={feature} delay={index * 100}>
                <div className="p-6 rounded-xl border border-gray-200 hover:border-blue-500/50 transition-colors">
                  <CircleCheck className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="text-lg font-medium mb-2">{feature}</h3>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-16">
              Our Recent Projects
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item, index) => (
              <ScrollReveal key={item.title} delay={index * 100}>
                <div className="group overflow-hidden rounded-xl bg-white shadow-lg">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-medium mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="aspect-square rounded-xl overflow-hidden">
                <img
                  src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/evan_profile.jpg"
                  alt="Evan - Founder of Astrem"
                  className="w-full h-full object-cover"
                />
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-display font-medium">
                  Meet Evan, Your Web Design Expert
                </h2>
                <p className="text-lg text-gray-600">
                  With over a decade of experience in web design and digital marketing, I founded Astrem with a vision to help businesses succeed online. My approach combines technical expertise with creative design to deliver websites that not only look great but drive real business results.
                </p>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-blue-500 mr-2" />
                    <a href="tel:225-331-6114" className="text-gray-900 hover:text-blue-500 transition-colors">
                      225-331-6114
                    </a>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-blue-500 mr-2" />
                    <span className="text-gray-900">Baton Rouge, LA</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default WebDesignService;
